<script>
import { mapState } from 'pinia';

import { useUserStore } from '../../../store/useUserStore.js';

export default {
  name: 'CpDropdownMenu',
  components: {
    CpButton: require('../button').default,
    CpContactSupport: require('@resources/js/dialogs/ContactSupport').default,
  },
  directives: {
    margin: require('../directives/margins').margin,
    marginLeft: require('../directives/margins').marginLeft,
    marginRight: require('../directives/margins').marginRight,
  },
  props: {
    clickToggle: {
      type: Boolean,
      default: false,
    },
    campaignHqLink: { type: String, default: '' },
  },
  data() {
    return {
      isOpen: false,
    };
  },
  methods: {
    updateFixedPosition() {
      if (!this.isOpen) return;
      this.$nextTick(() => {
        const button = this.$el?.querySelector('.dropbtn');
        const dropdown = this.$el?.querySelector('.dropdown-content');
        if (!button || !dropdown) return;

        const rect = button.getBoundingClientRect();
        const height = dropdown.offsetHeight || 200;
        dropdown.style.setProperty('--dropdown-top', `${rect.bottom - height}px`);
        dropdown.style.setProperty('--dropdown-left', `${rect.right}px`);
      });
    },
    handleToggle() {
      if (this.clickToggle) {
        this.isOpen = !this.isOpen;
        if (this.isOpen) {
          this.updateFixedPosition();
        }
      }
    },
    handleMouseEnter() {
      if (!this.clickToggle) {
        this.isOpen = true;
        this.updateFixedPosition();
      }
    },
    handleMouseLeave() {
      if (!this.clickToggle) {
        const isDialogOpen = this.$refs.contactSupport?.showDialog === true;
        if (!isDialogOpen) {
          this.isOpen = false;
        }
      }
    },
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.updateFixedPosition();
        window.addEventListener('scroll', this.updateFixedPosition, true);
        window.addEventListener('resize', this.updateFixedPosition);
      } else {
        window.removeEventListener('scroll', this.updateFixedPosition, true);
        window.removeEventListener('resize', this.updateFixedPosition);
      }
    },
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.updateFixedPosition, true);
    window.removeEventListener('resize', this.updateFixedPosition);
  },
  computed: {
    ...mapState(useUserStore, ['isUserLogin']),
  },
};
</script>
<template>
  <div class="dropdown" :class="{ 'click-toggle': clickToggle }" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
    <a class="dropbtn" @click="handleToggle">
      <i class="fal fa-question-circle fa-1-5x"></i>
      <span v-if="!$root.isMobile" class="subhead" v-margin-left="0.5">About CrowdBlue</span>
    </a>

    <!-- Dropdown content for hover mode -->
    <div v-if="!clickToggle" class="dropdown-content fixed-position" :class="{ show: isOpen }">
      <a href="/about">About Us</a>
      <CpContactSupport ref="contactSupport" />
      <a href="/campaigns/learn">How Campaigns Work</a>
      <a href="/about/privacy">Privacy Policy</a>
      <a href="/about/terms">Terms of Use</a>
      <a href="/community-guidelines">Community Guidelines</a>
      <a :href="campaignHqLink" target="_blank" rel="noopener noreferrer">CrowdBlue Campaign HQ</a>
      <a href="/pages/were-a-proud-member-of-connectblue">Advertise with Us</a>
      <a v-if="!isUserLogin" href="/how-it-works">How It Works</a>
    </div>

    <!-- Inline content for click toggle mode - styled like sidebar menu -->
    <div v-if="clickToggle" v-show="isOpen" class="more-options-container">
      <a href="/about">About Us</a>
      <CpContactSupport />
      <a href="/campaigns/learn">How Campaigns Work</a>
      <a href="/about/privacy">Privacy Policy</a>
      <a href="/about/terms">Terms of Use</a>
      <a href="/community-guidelines">Community Guidelines</a>
      <a :href="campaignHqLink" target="_blank" rel="noopener noreferrer">CrowdBlue Campaign HQ</a>
      <a href="/pages/were-a-proud-member-of-connectblue">Advertise with Us</a>
      <a v-if="!isUserLogin" href="/how-it-works">How It Works</a>
    </div>
  </div>
</template>

<style scoped>
.dropdown {
  position: relative;
  display: inline-block;
}

.dropdown,
.dropdown:hover {
  cursor: pointer;
}

.dropbtn {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  padding: 5px;
}

.dropdown-content {
  display: none;
  position: absolute;
  background-color: var(--white);
  width: max-content;
  min-width: 160px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  z-index: 1;
  left: 100%;
  bottom: 0px;
  border-radius: var(--internal-border-radius);
}

.dropdown-content.fixed-position {
  position: fixed;
  top: var(--dropdown-top, auto);
  left: var(--dropdown-left, auto);
  bottom: auto;
}

.dropdown-content.show {
  display: block;
}

.dropdown-content a,
.dropdown-content :deep(.contact-support-link) {
  color: black;
  padding: 12px;
  text-decoration: none;
  display: block;
  border-bottom: 1px solid var(--neutral-1);
}

.dropdown-content a:last-child {
  border-bottom: none;
}

.dropdown-content a:hover,
.dropdown-content :deep(.contact-support-link):hover {
  color: var(--primary-3);
  background-color: var(--primary-1);
}
.dropdown-content a:hover:first-child {
  border-radius: var(--internal-border-radius) var(--internal-border-radius) 0 0;
}
.dropdown-content a:hover:last-child {
  border-radius: 0 0 var(--internal-border-radius) var(--internal-border-radius);
}

.more-options-container {
  display: flex;
  padding-left: 30px;
  padding-top: 12px;
  flex-direction: column;
  gap: 12px;
}

.more-options-container a,
.more-options-container :deep(.contact-support-link) {
  color: var(--black);
  font-size: 17px;
  font-weight: bold;
  cursor: pointer;
  text-decoration: none;
  padding: 4px;
  border-radius: var(--internal-border-radius);
}

.more-options-container a:hover,
.more-options-container :deep(.contact-support-link):hover {
  color: var(--primary-3);
  background-color: var(--primary-1);
}

.dropdown:not(.click-toggle):hover .dropdown-content {
  display: block;
}

.dropdown:not(.click-toggle):hover .dropbtn {
  color: var(--primary-3);
  background-color: var(--primary-1);
  border-radius: 4px;
}

/* Click behavior */
.dropdown.click-toggle .dropbtn {
  transition: all 0.3s;
}

.dropdown.click-toggle .dropbtn:hover,
.dropdown.click-toggle .dropbtn.active {
  color: var(--primary);
  background-color: var(--primary-1);
  border-radius: 4px;
}

@media screen and (max-width: 1025px) {
  .dropbtn {
    padding-left: 10px;
  }
}

@media screen and (max-width: 768px) {
  .dropbtn {
    padding-left: 4px;
  }
}
</style>
